import React, {
  ReactNode,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import classNames from 'classnames';
import useMatchMedia from 'hooks/useMatchMedia';
import { Button, Overlay } from 'ncoded-component-library';
import Portal from 'components/Portal';
import { INITIAL_POSITION } from 'data/global';
import { Position, POSITION } from 'models/Position';
import { ObjectValues, XOR } from 'types';
import { getScrollParent } from 'utils';
import XIcon from 'icons/X.icon';

import './FloatingBanner.styles.scss';

const ARROW_WIDTH = 12;
const MOBILE_HORIZONTAL_OFFSET = 16;

export const FLOATING_BANBER_COLORS = {
  GREEN: 'green',
  BLACK: 'black',
};

type FlaotingBannerColors = ObjectValues<typeof FLOATING_BANBER_COLORS>;

export type FloatingBannerProps = {
  className?: string;
  color?: FlaotingBannerColors;
  showOnPhablet?: boolean;
  hasCloseButton?: boolean;
  hasOverlay?: boolean;
  onClose?: () => void;
} & XOR<{ anchor: ReactNode }, { selector: string }> &
  XOR<{ onHover: boolean }, { showBannerNotification?: boolean }>;

const FloatingBanner: React.FC<FloatingBannerProps> = (props) => {
  const {
    className,
    children,
    anchor,
    selector,
    showBannerNotification,
    onHover,
    showOnPhablet,
    hasOverlay,
    hasCloseButton = true,
    color = FLOATING_BANBER_COLORS.GREEN,
    onClose,
  } = props;

  const isPhablet = useMatchMedia('isPhablet');
  const showBanner = showBannerNotification && (showOnPhablet || !isPhablet);

  const [position, setPosition] = useState(INITIAL_POSITION);
  const [isInViewport, setIsInViewport] = useState(onHover || showBanner);
  const [isVisible, setIsVisible] = useState(showBanner);

  const bannerPosition = useRef<Position>(POSITION.RIGHT);
  const isLeft = useRef<boolean>(true);

  const anchorRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);

  const classes = classNames(
    'floating-banner',
    color,
    bannerPosition.current,
    { 'arrow-left': isLeft.current },
    className,
  );

  const calculateAndUpdateBannerPosition = useCallback(
    (anchorRect: DOMRect, contentRect: DOMRect) => {
      // TODO: refactor logic
      const viewportWidth = window.innerWidth;

      const contentWidth = contentRect.width;
      const contentHeight = contentRect.height;

      if (!contentWidth || !contentHeight) return;

      const spaceOnRight = viewportWidth - (anchorRect.x + anchorRect.width);

      const hasSpaceOnRight = spaceOnRight >= contentWidth + ARROW_WIDTH;
      const hasSpaceOnLeft = anchorRect.x >= contentWidth + ARROW_WIDTH;
      const hasSpaceBelow =
        window.innerHeight -
          (anchorRect.bottom + contentHeight + ARROW_WIDTH) >=
        0;

      const centeredTop =
        anchorRect.top +
        window.scrollY +
        anchorRect.height / 2 -
        contentHeight / 2;

      const hasSpaceAboveCentered = centeredTop >= 0;

      const hasSpaceBelowCentered =
        centeredTop + contentHeight <= window.innerHeight + window.scrollY;

      const centeredLeft =
        anchorRect.left +
        window.scrollX +
        anchorRect.width / 2 -
        contentWidth / 2;

      const hasSpaceOnLeftCentered = centeredLeft >= 0;

      let left;
      let top;

      if (
        (hasSpaceOnLeft || hasSpaceOnRight) &&
        hasSpaceAboveCentered &&
        hasSpaceBelowCentered
      ) {
        //  Positioning on the left or right

        left = hasSpaceOnRight
          ? anchorRect.left + anchorRect.width + ARROW_WIDTH // right position
          : anchorRect.left - contentWidth - ARROW_WIDTH; // left position
        top =
          anchorRect.y +
          window.scrollY +
          (anchorRect.height - contentHeight) / 2;

        bannerPosition.current = hasSpaceOnRight
          ? POSITION.RIGHT
          : POSITION.LEFT;

        isLeft.current = hasSpaceOnRight;
      } else {
        //  Positioning above or below

        const hasSpaceOnRightBelow =
          spaceOnRight + anchorRect.width >= contentWidth;
        const shouldBeLeft = isPhablet || !hasSpaceOnLeftCentered;

        left = shouldBeLeft
          ? hasSpaceOnRightBelow
            ? anchorRect.left - MOBILE_HORIZONTAL_OFFSET
            : anchorRect.left +
              anchorRect.width -
              contentWidth +
              MOBILE_HORIZONTAL_OFFSET
          : anchorRect.left + anchorRect.width / 2 - contentWidth / 2;

        top =
          isPhablet && !hasSpaceOnRightBelow
            ? anchorRect.top +
              window.scrollY -
              contentHeight -
              MOBILE_HORIZONTAL_OFFSET
            : hasSpaceBelow
            ? anchorRect.bottom + window.scrollY + ARROW_WIDTH // bottom position
            : anchorRect.top + window.scrollY - contentHeight - ARROW_WIDTH; // top position

        isLeft.current = shouldBeLeft && hasSpaceOnRightBelow;
        bannerPosition.current = hasSpaceBelow ? POSITION.BOTTOM : POSITION.TOP;
      }

      setPosition({ left, top });
    },
    [isPhablet],
  );

  const updateIfAnchorInViewport = useCallback(
    (anchor: HTMLElement) => {
      if (onHover) return;

      const scrollContainer = getScrollParent(anchor);
      const anchorRect = anchor.getBoundingClientRect();
      const containerRect = scrollContainer.getBoundingClientRect();

      const inXViewport =
        anchorRect.right > containerRect.left &&
        anchorRect.left < containerRect.right;

      const inYViewport =
        anchorRect.bottom > containerRect.top &&
        anchorRect.top < containerRect.bottom;

      setIsInViewport(inXViewport && inYViewport);
    },
    [onHover],
  );

  const updateBannerPosition = useCallback(() => {
    const anchor = anchorRef?.current;
    const content = contentRef?.current;

    if (!anchor || !content) return;

    const anchorRect = anchor.getBoundingClientRect();
    const contentRect = content.getBoundingClientRect();

    updateIfAnchorInViewport(anchor);
    calculateAndUpdateBannerPosition(anchorRect, contentRect);
  }, [calculateAndUpdateBannerPosition, updateIfAnchorInViewport]);

  const onMouseEnter = useCallback(() => setIsVisible(true), []);
  const onMouseLeave = useCallback(() => setIsVisible(false), []);

  const setAncorRef = useCallback(() => {
    if (selector) {
      const el = document.querySelector(selector) as HTMLDivElement;

      if (el) {
        anchorRef.current = el;

        if (onHover) {
          el.classList.add('clickable');
          el.addEventListener('mouseenter', onMouseEnter);
          el.addEventListener('mouseleave', onMouseLeave);

          return () => {
            el.removeEventListener('mouseenter', onMouseEnter);
            el.removeEventListener('mouseleave', onMouseLeave);
          };
        }
      }
    }
  }, [selector, onHover, onMouseEnter, onMouseLeave]);

  useEffect(() => {
    if (isVisible) {
      setAncorRef();
      updateBannerPosition();

      window.addEventListener('scroll', updateBannerPosition, true);
      window.addEventListener('resize', updateBannerPosition, true);

      return () => {
        window.removeEventListener('scroll', updateBannerPosition, true);
        window.removeEventListener('resize', updateBannerPosition, true);
      };
    }
  }, [isVisible, anchor, selector, setAncorRef, updateBannerPosition]);

  useEffect(() => {
    if (onHover) return;

    setIsVisible(showBannerNotification);
  }, [onHover, showBannerNotification]);

  return (
    <>
      {anchor && (
        <div
          className={classNames('floating-banner__anchor', {
            clickable: onHover,
          })}
          ref={anchorRef}
          {...(onHover && {
            onMouseEnter,
            onMouseLeave,
          })}
        >
          {anchor}
        </div>
      )}

      {hasOverlay && !onHover && (
        <Overlay
          background="transparent"
          className="floating-banner__overlay"
          open
          noContent
          overlayCloses
          lockScroll
          onClose={onClose}
        />
      )}

      <Portal isOpen={isInViewport && isVisible}>
        <div className={classes} style={position} ref={contentRef}>
          {children}

          {hasCloseButton && (
            <Button
              variant="icon"
              className="floating-banner__close"
              icon={XIcon}
              onClick={(e) => {
                e.stopPropagation();
                setIsInViewport(false);
                onClose?.();
              }}
            />
          )}
        </div>
      </Portal>
    </>
  );
};

export default FloatingBanner;
